import { openDB, DBSchema, IDBPDatabase } from 'idb';
import type { BusinessCard } from './types';

interface CardDB extends DBSchema {
  cards: {
    key: string;
    value: BusinessCard;
    indexes: { 'by-wallet': string; 'by-date': string };
  };
  settings: {
    key: string;
    value: any;
  };
}

let db: IDBPDatabase<CardDB>;

// Initialize IndexedDB
async function initDB() {
  if (!db) {
    db = await openDB<CardDB>('BusinessCardDB', 1, {
      upgrade(db) {
        // Create cards store
        if (!db.objectStoreNames.contains('cards')) {
          const cardStore = db.createObjectStore('cards', {
            keyPath: 'localId',
          });
          cardStore.createIndex('by-wallet', 'walletAddress');
          cardStore.createIndex('by-date', 'updatedAt');
        }

        // Create settings store
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings');
        }
      },
    });
  }
  return db;
}

// Save card to local storage
export async function saveCardLocally(card: BusinessCard): Promise<void> {
  const db = await initDB();
  await db.put('cards', card);
}

// Get all cards for a wallet
export async function getLocalCardsByWallet(walletAddress: string): Promise<BusinessCard[]> {
  const db = await initDB();
  const cards = await db.getAllFromIndex('cards', 'by-wallet', walletAddress);
  return cards.sort((a, b) =>
    new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

// Get a single card by ID
export async function getLocalCardById(localId: string): Promise<BusinessCard | undefined> {
  const db = await initDB();
  return db.get('cards', localId);
}

// Delete a card from local storage
export async function deleteLocalCard(localId: string): Promise<void> {
  const db = await initDB();
  await db.delete('cards', localId);
}

// Update a card in local storage
export async function updateLocalCard(card: BusinessCard): Promise<void> {
  const db = await initDB();
  card.updatedAt = new Date().toISOString();
  await db.put('cards', card);
}

// Get all local cards
export async function getAllLocalCards(): Promise<BusinessCard[]> {
  const db = await initDB();
  return db.getAll('cards');
}

// Save setting
export async function saveSetting(key: string, value: any): Promise<void> {
  const db = await initDB();
  await db.put('settings', value, key);
}

// Get setting
export async function getSetting(key: string): Promise<any> {
  const db = await initDB();
  return db.get('settings', key);
}

// Check if we have any cards saved
export async function hasLocalCards(): Promise<boolean> {
  const db = await initDB();
  const count = await db.count('cards');
  return count > 0;
}